import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { UserContext } from "../contexts/UserContext";
import { useTrackingConsent } from "./useTrackingConsent";
import TrackingConsentBanner from "../components/modals/TrackingConsentBanner";

const useActiveCampaignTracking = function () {
  const location = useLocation();
  const { user, hasUser } = useContext(UserContext);
  const { hasConsented, shouldShowConsent, acceptConsent, declineConsent } =
    useTrackingConsent();
  const [showConsentModal, setShowConsentModal] = useState(false);

  useEffect(() => {
    function injectActiveCampaignScript(
      windowObj,
      documentObj,
      scriptUrl,
      globalAlias,
    ) {
      windowObj.visitorGlobalObjectAlias = globalAlias;
      windowObj[globalAlias] =
        windowObj[globalAlias] ||
        function () {
          (windowObj[globalAlias].q = windowObj[globalAlias].q || []).push(
            arguments,
          );
        };
      windowObj[globalAlias].l = new Date().getTime();

      if (documentObj.getElementById("active-campaign-diffuser")) {
        return;
      }

      const scriptElement = documentObj.createElement("script");
      scriptElement.src = scriptUrl;
      scriptElement.async = true;
      scriptElement.id = "active-campaign-diffuser";

      const firstScript = documentObj.getElementsByTagName("script")[0];
      firstScript.parentNode.insertBefore(scriptElement, firstScript);
    }

    // Only inject script if user has consented to tracking
    if (hasConsented) {
      injectActiveCampaignScript(
        window,
        document,
        "https://diffuser-cdn.app-us1.com/diffuser/diffuser.js",
        "vgo",
      );

      if (window.vgo) {
        window.vgo(
          "setAccount",
          process.env.REACT_APP_ACTIVE_CAMPAIGN_TRACKING_CODE,
        );
        window.vgo("setTrackByDefault", true);
        window.vgo("setSiteAllowParameter", true);
      }
    }

    return () => {
      const script = document.getElementById("active-campaign-diffuser");
      if (script) {
        script.parentNode.removeChild(script);
      }

      delete window.vgo;
      delete window.visitorGlobalObjectAlias;
    };
  }, [hasConsented]);

  useEffect(() => {
    if (hasConsented && hasUser()) {
      if (window.vgo) {
        window.vgo("setEmail", user.email);

        if (window.visitorGlobalObject) {
          const visitor = window.visitorGlobalObject._getVisitor();
          if (visitor && visitor.updateUrl) {
            visitor.updateUrl(location.pathname);
          }
        }

        window.vgo("process");
      }
    }
  }, [location, user, hasUser, hasConsented]);

  useEffect(() => {
    // Suppress consent modal on privacy policy page
    if (location.pathname === "/privacy-policy") {
      setShowConsentModal(false);
      return;
    }

    if (shouldShowConsent) {
      const timer = setTimeout(() => {
        setShowConsentModal(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [shouldShowConsent, location.pathname]);

  const handleCloseModal = () => {
    setShowConsentModal(false);
  };

  const handleAccept = () => {
    acceptConsent();
    handleCloseModal();
  };

  const handleDecline = () => {
    declineConsent();
    handleCloseModal();
  };

  return (
    <TrackingConsentBanner
      open={showConsentModal}
      onAccept={handleAccept}
      onDecline={handleDecline}
    />
  );
};

export default useActiveCampaignTracking;
